import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useProviderAuth } from '../../context/ProviderAuthContext';
import { Eye, EyeOff, ArrowRight, Building2 } from 'lucide-react';
import MeridianLogo from '../../components/MeridianLogo';

export default function ProviderLoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { providerLogin } = useProviderAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await providerLogin(form.email, form.password);
      navigate('/provider-portal/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/"><MeridianLogo variant="dark" /></Link>

        <div>
          <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center mb-8">
            <Building2 size={22} className="text-white" />
          </div>
          <h2 className="text-4xl font-medium text-white mb-6 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
            Provider Portal
          </h2>
          <p className="text-[#A8B4B8] text-lg mb-12 leading-relaxed">
            Track patient referral leads, monitor procedure demand, and manage your Meridian presence — all in one place.
          </p>
          <div className="space-y-4">
            {[
              'View patient leads routed to your facility',
              'Monitor weekly lead volume and trends',
              'See which procedures drive the most inquiries',
              'Connect with Meridian to become a referral partner',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-[#0D3D4A] rounded-full flex-shrink-0" />
                <span className="text-[#A8B4B8] text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[#F0EEE9]/40 text-xs">
          Patient-facing app? <Link to="/" className="text-[#A8B4B8] hover:text-[#F0EEE9]/90">Go to Meridian &rarr;</Link>
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Provider Portal</p>
            <h1 className="text-3xl font-medium text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Sign in to your portal</h1>
            <p className="text-stone-500 text-sm">Access your lead dashboard, stats, and partnership tools.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="provider-login-form">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="provider@facility.com"
                className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                data-testid="provider-email-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-12 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                  data-testid="provider-password-input"
                  required
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="provider-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="provider-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In to Portal'}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-stone-500">
              No account yet?{' '}
              <Link to="/provider-portal/register" className="text-[#0D3D4A] font-medium hover:text-[#0D3D4A]" data-testid="go-to-register">
                Register your facility
              </Link>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <Link to="/" className="text-xs text-[#A8B4B8] hover:text-stone-600 transition-colors">
              &larr; Back to Meridian patient site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
